import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Inbox,
    Download,
    Trash2,
    Loader2,
    RefreshCw,
    Search,
    CheckCircle2,
    Circle,
    Mail,
    AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { formatBytes, formatDateTime } from "../lib/documents";
import api2 from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortableHead } from "../components/SortableHead";

export default function EmployeeUploadsPage() {
    const [items, setItems] = useState([]);
    const [newCount, setNewCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter && statusFilter !== "all") params.status = statusFilter;
            if (search.trim()) params.search = search.trim();
            const { data } = await api.get("/employee-uploads", { params });
            setItems(data.items || []);
            setNewCount(data.new_count || 0);
        } catch (e) {
            toast.error("Errore caricamento");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const handleDownload = async (item) => {
        setBusy(item.id);
        try {
            const res = await api.get(`/employee-uploads/${item.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = item.filename || "documento";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            await fetchData(); // refresh status
        } catch (e) {
            toast.error("Errore download");
        } finally {
            setBusy(null);
        }
    };

    const toggleStatus = async (item) => {
        const next = item.status === "new" ? "read" : "new";
        const fd = new FormData();
        fd.append("status", next);
        try {
            await api.put(`/employee-uploads/${item.id}/status`, fd);
            setItems((arr) => arr.map((i) => i.id === item.id ? { ...i, status: next } : i));
            toast.success(next === "read" ? "Segnato come letto" : "Riaperto");
        } catch (e) {
            toast.error("Errore");
        }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Eliminare "${item.title}"?`)) return;
        try {
            await api.delete(`/employee-uploads/${item.id}`);
            setItems((arr) => arr.filter((i) => i.id !== item.id));
            toast.success("Eliminato");
        } catch (e) {
            toast.error("Errore eliminazione");
        }
    };

    const { sortedItems: itemsSorted, sortKey, sortDir, requestSort } = useSortableTable(items, {
        customAccessors: {
            employee_label: (it) => (it.employee_label || "").toLowerCase(),
            title: (it) => (it.title || "").toLowerCase(),
        },
    });

    return (
        <div data-testid="employee-uploads-page">
            <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="relative flex h-12 w-12 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                            <Inbox className="h-6 w-6" />
                            {newCount > 0 && (
                                <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white" data-testid="new-badge">
                                    {newCount}
                                </span>
                            )}
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold text-[#376DB3]">Documenti dal dipendente</h1>
                            <p className="text-sm text-slate-600">
                                Documenti inviati dai dipendenti all'azienda tramite il portale.
                            </p>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} className="gap-2" data-testid="refresh-btn">
                        <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        Aggiorna
                    </Button>
                </div>

                {/* Filters */}
                <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-9 w-48" data-testid="status-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tutti gli stati</SelectItem>
                            <SelectItem value="new">Da leggere ({newCount})</SelectItem>
                            <SelectItem value="read">Già letti</SelectItem>
                        </SelectContent>
                    </Select>
                    <div className="relative min-w-[260px] flex-1">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Cerca titolo o nome file…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") fetchData(); }}
                            className="h-9 pl-8"
                            data-testid="search-input"
                        />
                    </div>
                    <Button size="sm" onClick={fetchData} disabled={loading} className="h-9 bg-[#376DB3] text-white hover:bg-[#376DB3]/90" data-testid="apply-btn">
                        Applica
                    </Button>
                </div>

                {/* Table */}
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <TableHead className="w-10"></TableHead>
                                <SortableHead sortKey="employee_label" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Dipendente
                                </SortableHead>
                                <SortableHead sortKey="title" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Documento
                                </SortableHead>
                                <SortableHead sortKey="uploaded_at" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="hidden md:table-cell">
                                    Inviato il
                                </SortableHead>
                                <SortableHead sortKey="size_bytes" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="hidden sm:table-cell text-right">
                                    Dim.
                                </SortableHead>
                                <TableHead className="text-right">Azioni</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={6} className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></TableCell></TableRow>
                            ) : itemsSorted.length === 0 ? (
                                <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-slate-500">
                                    <Mail className="mx-auto mb-2 h-10 w-10 text-slate-300" />
                                    Nessun documento ricevuto.
                                </TableCell></TableRow>
                            ) : itemsSorted.map((item) => {
                                const isNew = item.status === "new";
                                return (
                                    <TableRow key={item.id} className={isNew ? "bg-blue-50/40" : ""} data-testid={`upload-row-${item.id}`}>
                                        <TableCell>
                                            <button
                                                type="button"
                                                onClick={() => toggleStatus(item)}
                                                className="rounded p-1 hover:bg-slate-100"
                                                title={isNew ? "Segna come letto" : "Riapri"}
                                                data-testid={`toggle-status-${item.id}`}
                                            >
                                                {isNew ? (
                                                    <Circle className="h-4 w-4 text-blue-500 fill-blue-500" />
                                                ) : (
                                                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                                                )}
                                            </button>
                                        </TableCell>
                                        <TableCell>
                                            <Link
                                                to={`/dipendenti/${item.employee_id}`}
                                                className="font-medium text-[#376DB3] hover:underline"
                                                data-testid={`emp-link-${item.id}`}
                                            >
                                                {item.employee_label || "—"}
                                            </Link>
                                            <div className="text-xs text-slate-500">matr. {item.matricola || "—"}</div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="font-medium text-slate-800">{item.title}</div>
                                            <div className="text-xs text-slate-500" title={item.filename}>
                                                {item.filename}
                                            </div>
                                            {item.description && (
                                                <div className="mt-1 max-w-md text-xs italic text-slate-600">"{item.description}"</div>
                                            )}
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell font-mono-numbers text-xs text-slate-600">
                                            {formatDateTime(item.uploaded_at)}
                                            {item.status === "read" && item.read_at && (
                                                <div className="text-[10px] text-emerald-600">letto il {formatDateTime(item.read_at)}</div>
                                            )}
                                        </TableCell>
                                        <TableCell className="hidden sm:table-cell text-right font-mono-numbers text-xs text-slate-600">
                                            {formatBytes(item.size_bytes)}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => handleDownload(item)}
                                                    disabled={busy === item.id}
                                                    title="Scarica"
                                                    data-testid={`download-btn-${item.id}`}
                                                >
                                                    {busy === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                                </Button>
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    onClick={() => handleDelete(item)}
                                                    title="Elimina"
                                                    className="text-red-600 hover:bg-red-50"
                                                    data-testid={`delete-btn-${item.id}`}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
                <div className="mt-3 text-xs text-slate-500">
                    {items.length} documenti
                </div>
            </main>
        </div>
    );
}
